import { useCallback, useEffect, useState } from "react";
import { Button, Grid, Modal } from "semantic-ui-react";
import { EmergencyContact } from "../models/EmergencyContact";
import { FormError } from "../models/FormError";
import { GeoJSONPolygonType } from "../models/GeoJSONPolygonType";
import { Project } from "../models/Project";
import EditProjectForm, { FormData } from "./EditProjectForm";
import MapWithBoundaries from "./MapWithBoundaries";
import { Boundary } from "../models/Boundary";
import { mapGeoJSONPolygonTypeToBoundary } from "../utils/boundaryUtils";

interface Props {
  showProjectBoundariesOnMap?: boolean;
  open: boolean;
  onClose: () => void;
  onSubmit: (
    name: string,
    number: string,
    location: string,
    emergencyContact: EmergencyContact,
    boundaries?: GeoJSONPolygonType,
    id?: string
  ) => void;
  submitInProgress: boolean;
  submitErrors?: string[];
  project?: Project;
}

const EditProjectModal = ({
  showProjectBoundariesOnMap,
  open,
  onClose,
  onSubmit,
  submitInProgress,
  submitErrors,
  project,
}: Props) => {
  const [formData, setFormData] = useState<FormData>({} as FormData);
  const [formErrors, setFormErrors] = useState<FormError>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  //Don't change boundaries to boundary with this implementation
  //because it causes unwanted re-renders, and editing the boundary will not work properly.
  const [boundaries, setBoundaries] = useState<Boundary[]>([]);
  const [editBoundariesInProgress, setEditBoundariesInProgress] = useState(false);

  useEffect(() => {
    if (open && project) {
      setFormData({
        ...project,
        emergencyContact_name: project?.emergencyContact?.name,
        emergencyContact_phoneNumber: project?.emergencyContact?.phoneNumber,
      } as FormData);
      setBoundaries(project.boundaries ? [mapGeoJSONPolygonTypeToBoundary(project.boundaries)] : []);
    } else {
      setFormData({} as FormData);
      setFormErrors({} as FormError);
      setBoundaries([]);
      setHasSubmitted(false);
    }
  }, [open, project]);

  const validateFormData = (formData: FormData) => {
    const formError: FormError = {};

    if (!formData.name) {
      formError.name = "Please enter project name";
    }

    if (!formData.location) {
      formError.location = "Please enter project location";
    }

    if ((!boundaries || boundaries.length === 0) && showProjectBoundariesOnMap) {
      formError.boundaries = "Please draw project boundaries on map";
    }

    if (editBoundariesInProgress) {
      formError.boundaries =
        "Please complete the boundary modification by clicking outside its area on the map before submitting.";
    }

    return formError;
  };

  const handleSubmit = () => {
    setHasSubmitted(true);

    const validationErrors = validateFormData(formData);
    setFormErrors(validationErrors);

    // If there is no validation error
    if (Object.keys(validationErrors).length === 0) {
      const { name, number, location, emergencyContact_name, emergencyContact_phoneNumber, id } = formData;
      onSubmit(
        name,
        number,
        location,
        { name: emergencyContact_name, phoneNumber: emergencyContact_phoneNumber } as EmergencyContact,
        boundaries[0]?.polygon,
        id
      );
    }
  };

  const handleFormChange = (formData: FormData) => {
    if (hasSubmitted) {
      const validationErrors = validateFormData(formData);
      setFormErrors(validationErrors);
    }

    setFormData({ ...formData });
  };

  const handelClose = () => {
    setBoundaries([]);
    onClose();
  };

  const handleSaveBoundaries = useCallback((newBoundaries: Boundary[]) => {
    if (!newBoundaries || newBoundaries.length === 0) {
      setBoundaries([]);
      return;
    }
    setEditBoundariesInProgress(false);
    setBoundaries(newBoundaries);
  }, []);

  const handleBoundariesEditStarted = useCallback(() => {
    setEditBoundariesInProgress(true);
  }, []);

  return (
    <Modal open={open} size={showProjectBoundariesOnMap ? "fullscreen" : "small"}>
      <Modal.Header>Project</Modal.Header>
      <Modal.Content style={{ padding: "0px" }}>
        <Grid divided style={{ margin: "0px" }}>
          <Grid.Column stretched width={showProjectBoundariesOnMap ? "6" : "16"}>
            <EditProjectForm
              formErrors={formErrors}
              errors={submitErrors}
              formData={formData}
              onChange={handleFormChange}
            ></EditProjectForm>
          </Grid.Column>

          {showProjectBoundariesOnMap && (
            <Grid.Column width="10" style={{ padding: "0px" }}>
              <MapWithBoundaries
                withPopup={false}
                onBoundariesEditCompleted={handleSaveBoundaries}
                onBoundariesEditStarted={handleBoundariesEditStarted}
                boundaries={boundaries}
                allowMultipleBoundaries={false}
                height="500px"
              />
            </Grid.Column>
          )}
        </Grid>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handelClose}>Cancel</Button>
        <Button onClick={handleSubmit} positive loading={submitInProgress} disabled={submitInProgress}>
          Ok
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default EditProjectModal;
